/* Machine Menu: <details> gives click/tap toggling for free; this adds desktop
   hover-intent (open on enter, close on leave with a short grace delay),
   one-panel-at-a-time, and close on Esc or outside click — the same behaviour as
   Menu Beta. It also owns the pinning: the sticky class goes on the section
   wrapper (the only ancestor with room to travel) and a --pinned class on the
   nav drives the lifted shadow once the bar has reached the top. */
class EscMachineMenu extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('.esc-machine-menu');
    this.section = this.closest('.section-machine-menu');
    this.groups = Array.from(this.querySelectorAll('details'));
    this.hoverMedia = window.matchMedia('(hover: hover) and (min-width: 750px)');

    /* Below 750px the list and the CTA fold behind one labelled row. Only the
       attribute is flipped here — the CSS decides whether that means anything,
       so nothing has to be undone when the viewport grows past the breakpoint
       and the bar lays itself out again. */
    this.list = this.querySelector('.esc-machine-menu__list');
    this.collapseToggle = this.querySelector('.esc-machine-menu__collapse-toggle');
    if (this.collapseToggle) {
      this.collapseToggle.addEventListener('click', () => {
        this.setCollapsed(this.collapseToggle.getAttribute('aria-expanded') === 'true');
      });
    }

    this.groups.forEach((group) => {
      group.addEventListener('toggle', () => {
        group.querySelector('summary').setAttribute('aria-expanded', group.open);
        if (group.open) this.closeOthers(group);
      });

      group.addEventListener('mouseenter', () => {
        if (!this.hoverMedia.matches) return;
        clearTimeout(this.closeTimer);
        this.closeOthers(group);
        group.open = true;
      });

      group.addEventListener('mouseleave', () => {
        if (!this.hoverMedia.matches) return;
        this.closeTimer = setTimeout(() => {
          group.open = false;
        }, 180);
      });

      /* Menu Beta carries this guard and this bar did not, though the two run
         identical hover logic. On a hover-capable pointer, moving onto the
         summary fires mouseenter and opens the panel before the click lands;
         the browser's native summary toggle then closes what hover just
         opened, so the dropdown reads as not working at all. Hover owns
         open/close there, so swallow the click. Keyboard activation
         (detail === 0) and touch or narrow viewports still toggle normally. */
      group.querySelector('summary').addEventListener('click', (event) => {
        if (this.hoverMedia.matches && event.detail !== 0) {
          event.preventDefault();
        }
      });
    });

    /* setCollapsed closes the panels on the way, so these cover both layers:
       tapping the page or pressing Esc folds the bar back to one row. */
    this.onDocumentClick = (event) => {
      if (!this.owns(event.target)) this.setCollapsed(true);
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.setCollapsed(true);
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    if (this.dataset.sticky === 'true' && this.section) {
      this.section.classList.add('section-machine-menu--sticky');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.closeTimer);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      /* the wrapper reports its resting position; once that has passed the top
         of the viewport the nav inside it is being held at top: 0 */
      const pinned = this.section.getBoundingClientRect().top <= 0.5;
      this.nav.classList.toggle('esc-machine-menu--pinned', pinned);
      this.ticking = false;
    });
  }

  /* See the matching note in esc-menu-beta.js: below 750px this list is moved
     into Menu Alpha's panel, so `contains` alone would treat taps on our own
     submenus as outside clicks. */
  owns(node) {
    if (this.contains(node)) return true;
    return this.list ? this.list.contains(node) : false;
  }

  setCollapsed(collapsed) {
    if (collapsed) this.closeAll();
    if (!this.collapseToggle) return;
    this.collapseToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }

  closeOthers(current) {
    this.groups.forEach((group) => {
      if (group !== current) group.open = false;
    });
  }

  closeAll() {
    this.groups.forEach((group) => {
      group.open = false;
    });
  }
}

if (!customElements.get('esc-machine-menu')) {
  customElements.define('esc-machine-menu', EscMachineMenu);
}
